import { Sections } from "PFApp/types/activity_form/activity_form_schema";
import { Accordion } from "PFComponents/accordion";
import { Card } from "PFComponents/card";
import { Stack } from "PFComponents/containers";
import { useFirstRender } from "PFCore/hooks";
import React, { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";

import css from "../activity_form.module.scss";
import {
  useActivityFormStore,
  useActivityFormStoreActions
} from "../activity_form_store/use_activity_form_store";
import { focusSectionHeader, scrollToSection } from "../dom_utils";
import { useSectionsUtils } from "../hooks/use_sections_utils";

export type SectionProps = {
  id: Sections;
  title: ReactNode;
};

type FormSectionProps = SectionProps & {
  actions?: React.ReactNode;
  qaId?: string;
} & React.PropsWithChildren;

export const FormSection = ({ id, title, actions, qaId, children }: FormSectionProps) => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const currentSectionId = useActivityFormStore((state) => state.currentSectionId);
  const { setCurrentSectionId } = useActivityFormStoreActions();
  const { hasSectionErrors, isFirstInvalidSection } = useSectionsUtils();
  const isFirstRender = useFirstRender();

  const {
    formState: { submitCount }
  } = useFormContext();

  const [isExpanded, setIsExpanded] = useState(true);

  const focusSection = useCallback(() => {
    if (sectionRef?.current) {
      scrollToSection(sectionRef?.current);
      focusSectionHeader(sectionRef?.current);
    }
  }, []);

  useEffect(() => {
    if (isFirstRender) {
      return;
    }
    const hasFocusInside = sectionRef?.current?.contains(document.activeElement);
    if (id === currentSectionId && !hasFocusInside) {
      setIsExpanded(true);
      focusSection();
    }
  }, [currentSectionId]);

  useEffect(() => {
    if (hasSectionErrors(id)) {
      setIsExpanded(true);
    }

    if (isFirstInvalidSection(id)) {
      focusSection();
    }
  }, [hasSectionErrors(id), submitCount]);

  const handleSectionFocus = () => {
    setCurrentSectionId(id);
  };

  return (
    <Card ref={sectionRef} qaId={qaId} onFocus={handleSectionFocus} shadowVariant="none">
      <Accordion
        qaId={`${id}-accordion`}
        header={title}
        headerVariant="h4"
        iconSet="chevron"
        state={isExpanded ? "open" : "closed"}
        onStateChange={(state: string) => setIsExpanded(state === "open")}
        animated={false}
        keepMounted
        actions={actions}
        classes={{ header: css.sectionHeader }}
      >
        <Stack gap="spacingLg" role="presentation" tabIndex={-1}>
          {children}
        </Stack>
      </Accordion>
    </Card>
  );
};
